import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate, Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiUploadCloud, FiCopy, FiDownload, FiShare2, FiTrendingUp, FiAlertCircle } from 'react-icons/fi';
import { pageTransition, staggerContainer, staggerItem, tapScale, tapScaleTransition } from '../helpers/animations';
import { api, normalizeToken, tokenPath } from '../helpers';
import { API_BASE_URL } from '../helpers/config';
import { PaymentDetailsSkeleton } from '../components/skeletons';

function Buy() {
  const { token: tokenParam } = useParams();
  const token = normalizeToken(tokenParam);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mid = (searchParams.get('mid') || '').trim();
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState(null);
  const [loadingRate, setLoadingRate] = useState(false);
  const [minInr, setMinInr] = useState(null);
  const [maxInr, setMaxInr] = useState(null);
  const [upiChannels, setUpiChannels] = useState([]);
  const [selectedUpiId, setSelectedUpiId] = useState('');
  const [loadingUpi, setLoadingUpi] = useState(false);
  const [utr, setUtr] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!mid) {
      navigate(tokenPath(token, 'buy'), { replace: true });
      return;
    }

    setUpiChannels([]);
    setSelectedUpiId('');

    const fetchRate = async () => {
      if (!token) return;
      setLoadingRate(true);
      try {
        const { data } = await api.post(`${API_BASE_URL}/exchange/exchange-rate`, { token });
        if (data?.error === false && data?.data?.rate != null) {
          setRate(Number(data.data.rate));
        } else if (data?.error === 'session expired') {
          toast.error('Session expired. Please open your access link again.');
        } else if (data?.error) {
          toast.error(data.error);
        }
      } catch {
        // error toast handled by api helper
      } finally {
        setLoadingRate(false);
      }
    };

    const fetchUpiId = async () => {
      if (!token) return;
      setLoadingUpi(true);
      try {
        const { data } = await api.post(`${API_BASE_URL}/exchange/exchanger-upi-id`, { token, mid });
        if (data?.error === false && Array.isArray(data?.data)) {
          const channels = data.data
            .map((item) => ({
              upi_id: String(item?.upi_id || '').trim(),
              min: Number(item?.min),
              max: Number(item?.max),
              provider: String(item?.provider || '').trim(),
              mid: String(item?.mid ?? item?.merchant_id ?? '').trim(),
            }))
            .filter((item) => item.upi_id);
          setUpiChannels(channels);
          setSelectedUpiId(channels.length === 1 ? channels[0].upi_id : '');
        } else if (data?.error === 'session expired') {
          // session expired toast may already be shown from other calls
        } else if (data?.error) {
          toast.error(data.error);
        }
      } catch {
        // error toast handled by api helper
      } finally {
        setLoadingUpi(false);
      }
    };

    fetchRate();
    fetchUpiId();
  }, [mid, navigate, token]);

  const selectedChannel = upiChannels.find((channel) => channel.upi_id === selectedUpiId);

  useEffect(() => {
    if (selectedChannel) {
      setMinInr(Number.isFinite(selectedChannel.min) ? selectedChannel.min : null);
      setMaxInr(Number.isFinite(selectedChannel.max) ? selectedChannel.max : null);
    } else {
      setMinInr(null);
      setMaxInr(null);
    }
  }, [selectedChannel]);

  useEffect(() => {
    if (!amount) return;
    const numericAmount = Number(amount);
    let message = '';
    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      message = 'Please enter a valid amount';
    } else if (!selectedUpiId) {
      message = 'Please select a payment Marchent first';
    } else if (minInr != null && numericAmount < minInr) {
      message = `Minimum amount is ${minInr} INR`;
    } else if (maxInr != null && numericAmount > maxInr) {
      message = `Maximum amount is ${maxInr} INR`;
    }
    setErrors((prev) => ({ ...prev, amount: message }));
  }, [amount, selectedUpiId, minInr, maxInr]);

  const upiLink = selectedUpiId ? `upi://pay?pa=${encodeURIComponent(selectedUpiId)}&pn=TradeExchanger&cu=INR` : '';
  const qrDisplayUrl = upiLink
    ? `https://quickchart.io/qr?text=${encodeURIComponent(upiLink)}&size=420&margin=8&ecLevel=H&format=png&dark=000000&light=ffffff`
    : '';
  const qrDownloadUrl = upiLink
    ? `https://quickchart.io/qr?text=${encodeURIComponent(upiLink)}&size=1200&margin=12&ecLevel=H&format=png&dark=000000&light=ffffff`
    : '';

  const handleAmountChange = (e) => {
    const v = e.target.value.replace(/\D/g, '');
    setAmount(v);
    if (!v) {
      setErrors((prev) => ({ ...prev, amount: 'Please enter amount in INR' }));
      return;
    }

    const numericAmount = Number(v);
    let message = '';

    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      message = 'Please enter a valid amount';
    } else if (!selectedUpiId) {
      message = 'Please select a payment Marchent first';
    } else if (minInr != null && numericAmount < minInr) {
      message = `Minimum amount is ${minInr} INR`;
    } else if (maxInr != null && numericAmount > maxInr) {
      message = `Maximum amount is ${maxInr} INR`;
    }

    setErrors((prev) => ({ ...prev, amount: message }));
  };

  const handleCopyUpi = async () => {
    try {
      if (!selectedUpiId) {
        toast.error('UPI ID not available');
        return;
      }
      await navigator.clipboard.writeText(selectedUpiId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success('UPI ID copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDownloadQr = async () => {
    try {
      if (!qrDownloadUrl) {
        toast.error('QR code not available');
        return;
      }
      const res = await fetch(qrDownloadUrl, { mode: 'cors' });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'TradeExchanger-UPI-QR.png';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('QR code downloaded');
    } catch {
      if (qrDownloadUrl) {
        window.open(qrDownloadUrl, '_blank');
      }
      toast.error('Download failed');
    }
  };

  const handleShareQr = async () => {
    try {
      if (!qrDownloadUrl) {
        toast.error('QR code not available');
        return;
      }
      const res = await fetch(qrDownloadUrl, { mode: 'cors' });
      const blob = await res.blob();
      const file = new File([blob], 'TradeExchanger-UPI-QR.png', { type: 'image/png' });
      const canShare = navigator.share && (navigator.canShare ? navigator.canShare({ files: [file] }) : true);
      if (canShare) {
        await navigator.share({
          title: 'TradeExchanger UPI QR',
          text: selectedUpiId ? `Pay via UPI: ${selectedUpiId}` : 'Pay via UPI',
          files: [file],
        });
        toast.success('Shared successfully');
      } else {
        handleDownloadQr();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        handleDownloadQr();
        toast.error('Share failed');
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxBytes = 1024 * 1024; // 1 MB
    if (file.size > maxBytes) {
      setErrors((prev) => ({ ...prev, screenshot: 'Max screenshot size is 1 MB' }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      } else {
        e.target.value = '';
      }
      toast.error('Max screenshot size is 1 MB');
      return;
    }

    setScreenshot(file);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, screenshot: '' }));
  };

  const handleRemove = () => {
    setScreenshot(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setErrors((prev) => ({ ...prev, screenshot: '' }));
  };

  const handleConfirmSubmit = async () => {
    if (submitting) return;

    if (!token) {
      toast.error('Session expired. Please open your access link again.');
      setShowConfirm(false);
      return;
    }

    const formData = new FormData();
    formData.append('token', token);
    formData.append('amount', String(amount));
    formData.append('upi', selectedUpiId || '');
    formData.append('utr', utr.trim());
    if (screenshot) {
      formData.append('image', screenshot);
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(`${API_BASE_URL}/exchange/submit-exchange`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (data?.error === false) {
        toast.success(data?.msg || 'Exchange submitted successfully');
        setAmount('');
        setUtr('');
        setScreenshot(null);
        if (preview) URL.revokeObjectURL(preview);
        setPreview(null);
        setErrors({});
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setShowConfirm(false);
        navigate(tokenPath(token, 'buy-report'));
      } else {
        toast.error(data?.error || 'Failed to submit exchange');
      }
    } catch {
      // API helper will show a toast on network/server errors
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitExchange = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!amount) {
      newErrors.amount = 'Please enter amount in INR';
    }

    const numericAmount = Number(amount);
    if (!newErrors.amount && (Number.isNaN(numericAmount) || numericAmount <= 0)) {
      newErrors.amount = 'Please enter a valid amount';
    }

    if (!newErrors.amount && !selectedUpiId) {
      newErrors.amount = 'Please select a payment Marchent first';
    }

    if (!newErrors.amount && minInr != null && numericAmount < minInr) {
      newErrors.amount = `Minimum amount is ${minInr} INR`;
    }

    if (!newErrors.amount && maxInr != null && numericAmount > maxInr) {
      newErrors.amount = `Maximum amount is ${maxInr} INR`;
    }

    const utrValue = utr.trim();
    const screenshotFile = fileInputRef.current?.files?.[0];

    if (!selectedUpiId) {
      newErrors.upi = 'Please select a UPI Marchent';
    }

    if (!utrValue) {
      newErrors.utr = 'UTR / reference is required';
    }

    if (!screenshotFile) {
      newErrors.screenshot = 'Screenshot is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setShowConfirm(true);
  };

  return (
    <motion.div
      {...pageTransition}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      <motion.h1 className="screen-title" variants={staggerItem} initial="initial" animate="animate">
        Buy USDT
      </motion.h1>
      <motion.p
        className="buy-merchant-back"
        variants={staggerItem}
        initial="initial"
        animate="animate"
      >
        <Link to={tokenPath(token, 'buy')}>Change Marchent</Link>
      </motion.p>

      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.25 }}
      >
        {(loadingRate || loadingUpi) && !selectedUpiId && upiChannels.length === 0 ? (
          <PaymentDetailsSkeleton />
        ) : (
          <>
        <h2 className="card-title">Payment details</h2>
        <p className="card-subtitle">
          Use the UPI ID or QR code below.
        </p>
        <div className="rate-summary">
          {loadingRate && (
            <div className="rate-summary-row">
              <FiTrendingUp size={14} className="rate-summary-icon" />
              <span className="rate-summary-label">Fetching latest rate...</span>
            </div>
          )}
          {!loadingRate && rate && (
            <>
              <div className="rate-summary-row">
                <FiTrendingUp size={14} className="rate-summary-icon" />
                <span className="rate-summary-label">
                  <span className="rate-summary-muted">Live rate</span>
                  <strong className="rate-summary-strong">
                    {rate} INR = 1 USDT
                  </strong>
                </span>
              </div>
              {minInr != null && maxInr != null && (
                <div className="rate-summary-row rate-summary-row-secondary">
                  <FiAlertCircle size={14} className="rate-summary-icon" />
                  <span className="rate-summary-label">
                    <span className="rate-summary-muted">Allowed INR range</span>
                    <strong className="rate-summary-strong">
                      {minInr} - {maxInr} INR
                    </strong>
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        {upiChannels.length > 1 && (
          <div className="upi-row upi-row-channel" style={{ marginTop: '5px' }}>
            <span className="upi-label">Select Marchent</span>
            <div className={`upi-channel-list ${errors.upi ? 'input-error' : ''}`}>
              {loadingUpi ? (
                <div className="upi-channel-empty">Fetching Marchents...</div>
              ) : (
                upiChannels.map((channel) => {
                  const isActive = selectedUpiId === channel.upi_id;
                  return (
                    <button
                      key={channel.upi_id}
                      type="button"
                      className={`upi-channel-item ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedUpiId(channel.upi_id);
                        setErrors((prev) => ({ ...prev, upi: '', amount: '' }));
                      }}
                    >
                      <span className="upi-channel-id">{channel.upi_id}</span>
                      <span className="upi-channel-range">
                        Provider: {channel.provider || 'N/A'}
                      </span>
                      <span className="upi-channel-range">
                        ₹{Number.isFinite(channel.min) ? channel.min : '-'} - ₹{Number.isFinite(channel.max) ? channel.max : '-'}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            {errors.upi && <p className="field-error">{errors.upi}</p>}
          </div>
        )}
        {selectedUpiId ? (
          <>
            <div className="upi-row" style={{ marginTop: '10px' }}>
              <span className="upi-label">UPI ID</span>
              <div className="upi-value-wrap">
                <span className="upi-value">{selectedUpiId}</span>
                <motion.button
                  type="button"
                  className="upi-copy-btn"
                  onClick={handleCopyUpi}
                  whileTap={tapScale}
                  transition={tapScaleTransition}
                  title="Copy UPI ID"
                >
                  <FiCopy size={14} />
                  {copied ? 'Copied!' : 'Copy'}
                </motion.button>
              </div>
            </div>
            <div className="qr-section">
              <div className="qr-box">
                {qrDisplayUrl ? (
                  <img src={qrDisplayUrl} alt="UPI Payment QR Code" className="qr-code-img" />
                ) : (
                  <span className="upload-hint">QR will appear when UPI ID is available</span>
                )}
              </div>
              <div className="qr-actions">
                <motion.button
                  type="button"
                  className="qr-action-btn"
                  onClick={handleDownloadQr}
                  whileTap={tapScale}
                  transition={tapScaleTransition}
                >
                  <FiDownload size={14} />
                  Download
                </motion.button>
                <motion.button
                  type="button"
                  className="qr-action-btn"
                  onClick={handleShareQr}
                  whileTap={tapScale}
                  transition={tapScaleTransition}
                >
                  <FiShare2 size={14} />
                  Share
                </motion.button>
              </div>
            </div>
          </>
        ) : loadingUpi ? (
          <div className="upi-select-hint">Fetching payment details…</div>
        ) : upiChannels.length === 0 ? (
          <div className="upi-select-hint">No payment Marchents available</div>
        ) : (
          <div className="upi-select-hint">Please select payment Marchent</div>
        )}
          </>
        )}
      </motion.div>

      {selectedUpiId ? (
        <motion.form
          className="form"
          onSubmit={handleSubmitExchange}
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <h2 className="card-title">Exchange request</h2>

          <motion.div className="form-group" variants={staggerItem}>
            <label>Amount (INR)</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Enter amount"
              value={amount}
              onChange={handleAmountChange}
              className={errors.amount ? 'input-error' : ''}
            />
            {errors.amount && <p className="field-error">{errors.amount}</p>}
            {rate && amount && (
              <p className="field-hint">
                You will receive approximately{' '}
                <strong>
                  {(Number(amount) / rate).toFixed(2)} USDT
                </strong>
              </p>
            )}
            {Number(amount) > 2000 && (
              <p className="field-hint field-hint-success">Please copy UPI ID and make the payment.</p>
            )}
          </motion.div>

          <motion.div className="form-group" variants={staggerItem}>
            <label>Bank UTR / Reference no.</label>
            <input
              type="text"
              name="utr"
              placeholder="Enter UTR / reference"
              className={errors.utr ? 'input-error' : ''}
              value={utr}
              onChange={(event) => {
                const v = event.target.value;
                setUtr(v);
                if (errors.utr && v.trim()) {
                  setErrors((prev) => ({ ...prev, utr: '' }));
                }
              }}
            />
            {errors.utr && <p className="field-error">{errors.utr}</p>}
          </motion.div>

          <motion.div className="form-group" variants={staggerItem}>
            <label>Payment screenshot</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input-hidden"
              id="screenshot-upload"
            />
            <AnimatePresence mode="wait">
              {!preview ? (
                <motion.label
                  htmlFor="screenshot-upload"
                  className={`upload-zone ${errors.screenshot ? 'screenshot-error' : ''}`}
                  key="upload"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.01 }}
                  whileTap={tapScale}
                >
                  <FiUploadCloud size={28} className="upload-icon" />
                  <span>Tap to upload payment screenshot</span>
                  <span className="upload-hint">PNG, JPG up to 5MB</span>
                </motion.label>
              ) : (
                <motion.div
                  className={`screenshot-preview ${errors.screenshot ? 'screenshot-error' : ''}`}
                  key="preview"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  <img src={preview} alt="Payment screenshot" />
                  <div className="screenshot-actions">
                    <motion.button
                      type="button"
                      className="btn ghost btn-sm"
                      onClick={() => fileInputRef.current?.click()}
                      whileTap={tapScale}
                      transition={tapScaleTransition}
                    >
                      Change
                    </motion.button>
                    <motion.button
                      type="button"
                      className="btn ghost btn-sm btn-danger-text"
                      onClick={handleRemove}
                      whileTap={tapScale}
                      transition={tapScaleTransition}
                    >
                      Remove
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.button
            type="submit"
            className="btn primary"
            variants={staggerItem}
            whileTap={tapScale}
            transition={tapScaleTransition}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit exchange request'}
          </motion.button>
        </motion.form>
      ) : (
        <div className="card upi-form-lock-msg">Please select payment Marchent to continue with exchange form.</div>
      )}

      <AnimatePresence>
        {showConfirm && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="modal"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="modal-header">
                <h2 className="card-title">Review exchange</h2>
                <p className="card-subtitle">
                  Make sure everything matches your bank payment before you submit.
                </p>
              </div>

              <div className="modal-body">
                <div className="modal-summary-row">
                  <div className="modal-summary-chip">
                    <span className="modal-summary-label">Amount</span>
                    <span className="modal-summary-value">₹ {amount || '0'}</span>
                  </div>
                  {rate && amount && (
                    <div className="modal-summary-chip">
                      <span className="modal-summary-label">You receive</span>
                      <span className="modal-summary-value">
                        {(Number(amount) / rate).toFixed(2)} USDT
                      </span>
                    </div>
                  )}
                </div>

                <div className="modal-info-card">
                  <div className="modal-info-row">
                    <span className="modal-info-label">UPI ID</span>
                    <span className="modal-info-value">{selectedUpiId || 'Not available'}</span>
                  </div>
                  <div className="modal-info-row">
                    <span className="modal-info-label">Bank UTR / Ref.</span>
                    <span className="modal-info-value">{utr || '-'}</span>
                  </div>
                </div>

                {preview && (
                  <div className="modal-preview-card">
                    <span className="modal-info-label">Payment screenshot</span>
                    <div className="modal-preview-inner">
                      <img src={preview} alt="Payment screenshot preview" />
                    </div>
                  </div>
                )}

                <div className="modal-warning-card">
                  <div className="modal-warning-title">
                    <FiAlertCircle size={14} />
                    <span>Important</span>
                  </div>
                  <ul className="confirm-terms">
                    <li>Do not submit incorrect or fake payment details.</li>
                    <li>Payments are final. There are no refunds once processed.</li>
                    <li>Make sure the UTR, amount and screenshot match your bank transfer.</li>
                  </ul>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn ghost btn-sm"
                  onClick={() => setShowConfirm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn primary btn-sm"
                  onClick={handleConfirmSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Confirm & submit'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default Buy;

